/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import { first } from 'ckeditor5';

import { forEach } from 'lodash-es';

/**
 * @module bulb-block-alignment/converters
 */

/**
 * Returns the style with a given `name` from an array of styles.
 *
 * @param name
 * @param options
 * @returns style
 */
function getOptionByName(name, options) {
  return options.find((option) => option.name === name);
}

/**
 * Returns a converter for the `blockAlignment` attribute.
 * It can be used for adding, changing and removing the attribute.
 *
 * @param {Object} styles An object containing available styles.
 * @param editor editor instance.
 * @returns {Function} A model-to-view attribute converter.
 */
export function modelToViewStyleAttribute(styles, editor) {
  return (evt, data, conversionApi) => {
    if (!conversionApi.consumable.consume(data.item, evt.name)) {
      return;
    }

    // Check if there is class name associated with given value.
    const newStyle = getOptionByName(data.attributeNewValue, styles);
    const oldStyle = getOptionByName(data.attributeOldValue, styles);

    const viewElement = conversionApi.mapper.toViewElement(data.item);
    const viewWriter = conversionApi.writer;

    if (oldStyle) {
      viewWriter.removeClass(oldStyle.className, viewElement);
    }

    if (newStyle) {
      viewWriter.addClass(newStyle.className, viewElement);
      if (newStyle.className === 'image-alignment-left' || newStyle.className === 'block-alignment-left') {
        if (CKEBulbData.service.ckeStateService?.isEventsRegisterForMarginToCkeLists === false) {
          /**
           * Add margin to list elements on CK editor page.
           */
          setTimeout(() => CKEBulbData.service.addMarginToListForCKEditor);

          CKEBulbData.service.registerEventsForCkeLists(editor);

          // Set status true as events are registered.
          CKEBulbData.service.ckeStateService.isEventsRegisterForMarginToCkeLists = true;
        }
      }
    }
  };
}

/**
 * Returns a view-to-model converter converting image CSS classes to a proper value in the model.
 *
 * @param styles The styles for which the converter is created.
 * @returns {Function} A view-to-model converter.
 */
export function viewToModelStyleAttribute(styles) {
  // Convert only non–default styles.
  const filteredStyles = styles.filter((style) => !style.isDefault);

  return (evt, data, conversionApi) => {
    if (!data.modelRange) {
      return;
    }

    const viewFigureElement = data.viewItem;
    const modelBlockElement = first(data.modelRange.getItems());

    // Check if `imageStyle` attribute is allowed for current element.
    if (!conversionApi.schema.checkAttribute(modelBlockElement, 'blockAlignment')) {
      return;
    }

    // Convert style one by one.
    forEach(filteredStyles, (style) => {
      // Try to consume class corresponding with style.
      if (conversionApi.consumable.consume(viewFigureElement, { classes: style.className })) {
        // And convert this style to model attribute.
        conversionApi.writer.setAttribute('blockAlignment', style.name, modelBlockElement);
      }
    });
  };
}
